import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useMediaQuery } from '@mantine/hooks'
import dayjs from 'dayjs'
import { useCopyUrlShare } from 'shared/hooks/useCopyUrlShare'
import {
  section3DetailsList,
  curriculumDetailList,
  curriculumBenefitList,
  section9DetailsList,
  supportBenefitList,
  benefitList,
  reviewList,
  ApplyScheduleList,
  EduScheduleList,
  bootCampFaqList,
  bootCampFaqTabList,
} from '../../../../constants/kdt-growth-2nd'
import Link from 'next/link'
import Image from 'next/image'
import ReactPlayer from 'react-player'
import { useMemo, useState, useEffect } from 'react'

import CurriculumAccordion from './CurriculumAccordion'
import CurriculumTab from './CurriculumTab'
import styles from './kdt-growth-2nd.module.scss'
import { schoolNavList, ScrollNav } from './ScrollNav'
import {
  CurriculumBenefitSliderItem,
  SupportBenefitSliderItem,
  BenefitSliderItem,
  ReviewSliderItem,
} from './SliderItem'

interface ApplyScheduleItem {
  title: string
  content: string
  contentList?: string[]
}

interface IGrowthKlass2ndProps {
  applyStartedAt: string
  applyEndedAt: string
  startedAt: string
  endedAt: string
  totalSeconds: number
}

const GrowthKlass2nd = ({
  applyStartedAt,
  applyEndedAt,
  startedAt,
  endedAt,
  totalSeconds,
}: IGrowthKlass2ndProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [faqTab, setFaqTab] = useState('apply')
  const allFaqs = [
    ...bootCampFaqList.apply,
    ...bootCampFaqList.nebeka,
    ...bootCampFaqList.employment,
    ...bootCampFaqList.edu,
    ...bootCampFaqList.benefit,
    ...bootCampFaqList.etc,
  ]
  const faqList = faqTab === 'all' ? allFaqs : bootCampFaqList[faqTab]
  const onClickFaqTab = (value: string) => {
    setFaqTab(value)
  }

  const earlySessionDate = new Date('2025-04-07 21:00')
  const isAfterEarlySessionDate = useMemo(() => dayjs().isAfter(earlySessionDate), [])

  const [openedMidProject, setOpenedMidProject] = useState<string | null>(null)
  const [openedFinalProject, setOpenedFinalProject] = useState<string | null>(null)
  const [openedTitle, setOpenedTitle] = useState<string | null>(ApplyScheduleList[0]?.title || null)
  const { copyUrlShare } = useCopyUrlShare()

  const convertAmPm = (time: string) => {
    return time.replace('AM', '오전').replace('PM', '오후')
  }

  const applyStart = convertAmPm(dayjs(applyStartedAt).format('YY.MM.DD A h')) + '시'
  const applyEnd = convertAmPm(dayjs(applyEndedAt).format('YY.MM.DD A h')) + '시'
  const start = dayjs(startedAt).format('YY.MM.DD')
  const end = dayjs(endedAt).format('YY.MM.DD')
  const totalHours = Math.ceil(totalSeconds / 3600)

  return (
    <div>
      {/* Section1_키비주얼 */}
      <section className="bg-gray-900">
        <div
          data-aos-once="true"
          data-aos="fade-up"
          data-aos-easing="ease"
          data-aos-duration="800"
          data-aos-delay="300"
          className={`${styles.section1_kv} relative w-full`}
        >
          <div className="absolute left-0 top-0 w-full">
            <Image
              src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section1_kv_after_${
                isMobile ? 'm' : 'pc'
              }.png`}
              layout="fill"
              objectFit="cover"
              className="!relative w-full"
              alt="[멋쟁이사자처럼] 실전 경험으로 여는 그로스 마케팅 취업의 문"
            />
          </div>
        </div>
      </section>

      {/* Section2_기본정보 */}
      <section className="bg-gradient-to-b from-gray-900 to-[#0D0D0D] pb-6 pt-[60px] lg:py-[60px]">
        <div className="mx-auto w-full max-w-lg px-4 lg:max-w-7xl lg:px-6">
          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex w-full flex-col"
          >
            <li className="flex w-full flex-row items-start gap-[45px] border-b border-[#3D3D3D] pb-3 lg:items-center lg:gap-[231px] lg:px-0 lg:pb-4">
              <h6 className="flex flex-row items-center justify-start gap-3 text-sm font-semibold text-white lg:w-[185px] lg:gap-4 lg:text-base">
                <img
                  className="h-5 w-5 lg:h-6 lg:w-6"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section2_icon1.png"
                  alt="-"
                />
                모집기간
              </h6>
              <p className="flex-1 text-sm leading-normal text-label-assistive lg:text-base">
                {applyStart} ~ <br className="block lg:hidden" />
                {applyEnd}
              </p>
            </li>
            <li className="flex w-full flex-row items-start gap-[45px] border-b border-[#3D3D3D] py-3 lg:items-center lg:gap-[231px] lg:px-0 lg:py-4">
              <h6 className="flex flex-row items-center justify-start gap-3 text-sm font-semibold text-white lg:w-[185px] lg:gap-4 lg:text-base">
                <img
                  className="h-5 w-5 lg:h-6 lg:w-6"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section2_icon2.png"
                  alt="-"
                />
                교육기간
              </h6>
              <p className="flex-1 text-sm leading-normal text-label-assistive lg:text-base">
                {start} ~ {end}
              </p>
            </li>
            <li className="flex w-full flex-row items-start gap-[45px] border-b border-[#3D3D3D] py-3 lg:items-center lg:gap-[231px] lg:px-0 lg:py-4">
              <h6 className="flex flex-row items-center justify-start gap-3 text-sm font-semibold text-white lg:w-[185px] lg:gap-4 lg:text-base">
                <img
                  className="h-5 w-5 lg:h-6 lg:w-6"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section2_icon4.png"
                  alt="-"
                />
                교육시간
              </h6>
              <p className="flex-1 text-sm leading-normal text-label-assistive lg:text-base">
                평일 9시 ~ 18시 <br className="block lg:hidden" />
                (점심시간 12시 ~ 13시 제외)
              </p>
            </li>
            <li className="flex w-full flex-row flex-wrap items-start gap-x-[45px] gap-y-3.5 pt-3 lg:flex-nowrap lg:gap-x-[231px] lg:px-0 lg:pt-4">
              <h6 className="flex flex-row items-center justify-start gap-3 text-sm font-semibold text-white lg:w-[185px] lg:gap-4 lg:text-base">
                <img
                  className="h-5 w-5 lg:h-6 lg:w-6"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section2_icon3.png"
                  alt="-"
                />
                교육비용
              </h6>
              <p className="flex-1 text-sm leading-normal text-label-assistive lg:text-base">
                내일배움카드로 교육비 전액 무료 <br className="block lg:hidden" />
                <span className="line-through">11,035,200원</span>
                <br />
                매월 최대 81만원 훈련장려금 지급
              </p>
            </li>
          </ul>
        </div>
      </section>

      {/* Section3_핵심포인트 */}
      <section className="bg-gray-900 py-[34px] lg:py-[72px]">
        <div className="mx-auto w-full max-w-lg px-4 lg:max-w-7xl lg:px-6">
          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="mb-6 flex w-full flex-col gap-3 lg:mb-5 lg:flex-row lg:flex-wrap lg:gap-6"
          >
            {section3DetailsList.map(details => (
              <li className="flex w-full flex-row-reverse items-center gap-3 rounded-lg bg-trueGray-800 px-4 py-3 lg:lg:w-[calc(50%-12px)] lg:flex-row lg:gap-4 lg:px-6 lg:py-5">
                <img
                  className="h-[68px] w-[68px] overflow-hidden rounded object-cover lg:h-[90px] lg:w-[90px]"
                  src={details.imgUrl}
                  alt="-"
                />
                <div className="flex flex-1 flex-col gap-1 lg:gap-2">
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.1)] px-[7px] py-1 text-[10px] font-semibold text-yellow-450 lg:px-3 lg:text-xs lg:font-bold">
                    {details.step}
                  </p>
                  <h6 className="whitespace-pre-line text-sm font-semibold leading-normal text-label-assistive lg:text-lg">
                    {details.title}
                  </h6>
                </div>
              </li>
            ))}
          </ul>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="overflow-hidden rounded-lg border border-yellow-450 bg-[rgba(255,119,16,0.10)] px-4 py-3 lg:px-10 lg:py-8"
          >
            <div className="border-b border-[rgba(171,83,17,0.5)] pb-3 lg:pb-5">
              <h5 className="flex-1 text-base font-bold text-white lg:text-[28px]">지원조건</h5>
            </div>
            {/* open */}
            <ul className="mt-3 flex w-full flex-col lg:mt-5 lg:gap-2">
              <li
                className={`${styles.text_dot} ${styles.text_dot_section3} text-sm leading-normal text-white lg:text-base`}
              >
                국민내일배움카드 보유자 혹은 발급 가능하신 분
              </li>
              <li
                className={`${styles.text_dot} ${styles.text_dot_section3} text-sm leading-normal text-white lg:text-base`}
              >
                평일 오전 9시부터 오후 6시까지 수업 참여가 가능하신 분
              </li>
              <li
                className={`${styles.text_dot} ${styles.text_dot_section3} text-sm leading-normal text-white lg:text-base`}
              >
                직장 혹은 학교 등과 병행하지 않고 수업에만 집중 가능하신 분
              </li>
              <li
                className={`${styles.text_dot} ${styles.text_dot_section3} text-sm leading-normal text-white lg:text-base`}
              >
                국내에 거주하고 계신 분
              </li>
            </ul>
          </div>
        </div>
      </section>

      <ScrollNav schoolNavList={schoolNavList} />

      {/* Section4_why? */}
      {isAfterEarlySessionDate ? (
        <section className="bg-trueGray-800 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36">
          <div className="mx-auto w-full max-w-lg px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16"
            >
              <p className="text-sm font-bold text-yellow-450 lg:text-lg">WHY 그로스 마케터?</p>
              <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                급변하는 트렌드 속 <br className="block lg:hidden" />
                소비자를 사로잡기 위해
                <br />
                가장 필요한 기업 핵심 직무
              </h4>
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="flex flex-col overflow-hidden rounded-lg bg-[#303030] lg:flex-row"
            >
              <div className="w-full lg:w-1/2">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section4_img_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="cover"
                  className="!relative w-full"
                  alt="-"
                />
              </div>
              <div className="flex flex-col items-center justify-center p-4 lg:px-12">
                <div className="mb-5 hidden w-full items-center justify-start border-b border-[#525252] pb-5 lg:flex">
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.1)] px-2 py-1 text-base font-bold text-yellow-450">
                    Why?
                  </p>
                </div>
                <p className="w-full text-center text-sm leading-normal text-white lg:text-left lg:text-lg">
                  기업은 광고 성과를 측정하는 마케터에서 더 나아가{' '}
                  <br className="block lg:hidden" />
                  데이터를 활용해 <br className="hidden lg:block" />
                  고객 접점을 확대하고 브랜드와 <br className="block lg:hidden" />
                  제품 성장에 기여하는 마케터를 원해요.
                </p>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-trueGray-800 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36">
          <div className="mx-auto w-full max-w-lg px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16"
            >
              <p className="text-sm font-bold text-yellow-450 lg:text-lg">WHY 그로스 마케터?</p>
              <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                급변하는 트렌드 속 <br className="block lg:hidden" />
                소비자를 사로잡기 위해
                <br />
                가장 필요한 기업 핵심 직무
              </h4>
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="flex flex-col overflow-hidden rounded-lg bg-[#303030] lg:flex-row"
            >
              <div className="w-full lg:w-1/2">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section4_img_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="cover"
                  className="!relative w-full"
                  alt="-"
                />
              </div>
              <div className="flex flex-col items-center justify-center p-4 lg:items-start lg:justify-start lg:p-12">
                <div className="flex flex-1 flex-col items-start justify-start">
                  <p className="w-full text-center text-sm leading-normal text-white lg:text-left lg:text-lg">
                    기업은 광고 성과를 측정하는 마케터에서 더 나아가{' '}
                    <br className="block lg:hidden" />
                    데이터를 활용해 <br className="hidden lg:block" />
                    고객 접점을 확대하고 브랜드와 <br className="block lg:hidden" />
                    제품 성장에 기여하는 마케터를 원해요.
                  </p>
                  <p className="mt-5 hidden w-full border-t border-[#525252] pt-5 text-left text-base leading-normal text-white lg:flex">
                    그로스 마케터 취업에 궁금한 것이 있다면,
                    <br />
                    강사님께 실시간으로 무엇이든 물어보세요!
                  </p>
                </div>
                <Link
                  href="https://likelion2.typeform.com/kdtgm02"
                  target="_blank"
                  className="hidden w-full items-center justify-center gap-2 rounded-lg bg-yellow-450 py-3.5 text-sm font-bold text-white lg:flex lg:gap-2 lg:py-5 lg:text-lg"
                >
                  고민상담소 신청하기
                  <img
                    className="h-4 w-4 lg:h-5 lg:w-5"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-13th/pre-orientation_icon.png"
                    alt="go link"
                  />
                </Link>
              </div>
            </div>

            <p
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="mt-10 w-full text-center text-base font-bold leading-normal text-white lg:hidden"
            >
              그로스 마케터 취업에 궁금한 것이 있다면,
              <br />
              강사님께 실시간으로 무엇이든 물어보세요!
            </p>

            <Link
              href="https://likelion2.typeform.com/kdtgm02"
              target="_blank"
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-yellow-450 py-3.5 text-sm font-bold text-white lg:hidden"
            >
              고민상담소 신청하기
              <img
                className="h-4 w-4"
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-13th/pre-orientation_icon.png"
                alt="go link"
              />
            </Link>
          </div>
        </section>
      )}

      {/* Section5_커리큘럼 */}
      <section id="curriculum" className="bg-gray-900 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36">
        <div className="mx-auto w-full max-w-lg px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">커리큘럼</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-4xl">
              2025년 그로스 마케터가 되는 <br className="block lg:hidden" />
              가장 빠른 길
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
              주요 기업의 채용 트렌드를 전격 반영한 최신 커리큘럼으로
              <br />
              소중한 커리어의 첫 걸음을 제대로 시작해보세요.
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex flex-col"
          >
            <div className="flex flex-row items-stretch justify-center overflow-hidden rounded-t-lg border-b">
              <div className="flex w-1/2 flex-col items-center justify-center border-r bg-white p-4 lg:h-[150px] lg:px-6 lg:py-4">
                <div className="flex flex-col items-center justify-center gap-[4.45px] p-3 lg:flex-row lg:flex-wrap lg:gap-x-[22.7px] lg:gap-y-2 lg:p-0">
                  <img
                    className="h-[16.49px] w-auto object-contain lg:h-7"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section5_logo1.png"
                    alt="-"
                  />
                  <img
                    className="h-[17.54px] w-auto object-contain lg:h-[29.8px]"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section5_logo2.png"
                    alt="-"
                  />
                  <img
                    className="h-[12.9px] w-auto object-contain lg:h-[21.92px]"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section5_logo3.png"
                    alt="-"
                  />
                  <img
                    className="h-[13.48px] w-auto object-contain lg:h-[22.91px]"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section5_logo5.png"
                    alt="-"
                  />
                </div>
              </div>
              <div className="flex w-1/2 flex-col items-center justify-center bg-white p-4 lg:h-[150px] lg:px-6 lg:py-4">
                <div className="flex flex-col p-3 lg:flex-row lg:p-0">
                  <img
                    className="h-[11.78px] w-auto object-contain lg:h-[21.62px]"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section5_logo_likelion.png"
                    alt="-"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-row items-start overflow-hidden rounded-b-lg">
              <div className="flex w-1/2 flex-col items-center justify-center bg-strong px-3 py-4 lg:px-6 lg:pb-6 lg:pt-8">
                <h6 className="mb-4 text-center text-lg font-bold text-label-normal lg:mb-8 lg:text-2xl">
                  주요 기업 <br className="block lg:hidden" />
                  그로스 마케터 <br className="block sm:hidden" />
                  자격요건
                </h6>
                <ul className={`${styles.section5_mediaquery} flex w-full flex-col gap-2`}>
                  <li className="flex min-h-[87px] items-center justify-center rounded bg-white p-3 sm:min-h-[87px] lg:min-h-[94px] lg:p-5">
                    <p className="text-center text-sm leading-normal text-label-normal lg:text-lg">
                      의사결정에 필요한 <br className="sm:block lg:hidden" />
                      마케팅 데이터 <br className="sm:block lg:hidden" />
                      추출 및 분석
                    </p>
                  </li>
                  <li className="flex min-h-[66px] items-center justify-center rounded bg-white p-3 sm:min-h-[66px] lg:min-h-[94px] lg:p-5">
                    <p className="text-center text-sm leading-normal text-label-normal lg:text-lg">
                      데이터 분석을 통한 <br className="sm:block lg:hidden" />
                      인사이트 도출 경험
                    </p>
                  </li>
                  <li className="flex min-h-[87px] items-center justify-center rounded bg-white p-3 sm:min-h-[66px] lg:min-h-[94px] lg:p-5">
                    <p className="text-center text-sm leading-normal text-label-normal lg:text-lg">
                      서비스 기반의 <br className="block lg:hidden" />
                      AAARRR 계획 및 <br className="block sm:hidden" />
                      운영
                    </p>
                  </li>
                  <li className="flex min-h-[66px] items-center justify-center rounded bg-white p-3 sm:min-h-[66px] lg:min-h-[94px] lg:p-5">
                    <p className="text-center text-sm leading-normal text-label-normal lg:text-lg">
                      성과 관리를 위한 <br className="block sm:hidden" />
                      대시보드 설계/
                      <br className="block sm:hidden" />
                      생성/관리
                    </p>
                  </li>
                  <li className="flex min-h-[87px] items-center justify-center rounded bg-white p-3 sm:min-h-[66px] lg:min-h-[94px] lg:p-5">
                    <p className="text-center text-sm leading-normal text-label-normal lg:text-lg">
                      고객 마케팅 <br className="block sm:hidden" />
                      성공 경험
                    </p>
                  </li>
                </ul>
              </div>
              <div className="flex w-1/2 flex-col items-center justify-center bg-[#FFF1E8] px-3 py-4 lg:px-6 lg:pb-6 lg:pt-8">
                <h6 className="mb-4 text-center text-lg font-bold text-label-normal lg:mb-8 lg:text-2xl">
                  그로스 마케팅 <br className="block lg:hidden" />
                  부트캠프 <br className="block sm:hidden" />
                  커리큘럼
                </h6>
                <ul className={`${styles.section5_mediaquery} flex w-full flex-col gap-2`}>
                  <li className="flex items-center justify-center rounded bg-white p-3 sm:min-h-[87px] lg:min-h-[94px] lg:p-5">
                    <p className="text-center text-sm font-bold leading-normal text-label-normal lg:text-lg">
                      의사결정을 위한 <br className="block lg:hidden" />
                      데이터 분석 기초 및 <br className="block sm:hidden" />
                      방법론
                    </p>
                  </li>
                  <li className="flex items-center justify-center rounded bg-white p-3 lg:min-h-[94px] lg:p-5">
                    <p className="text-center text-sm font-bold leading-normal text-label-normal lg:text-lg">
                      데이터 기반 마케팅 <br className="block lg:hidden" />
                      결과 예측 및 개선
                    </p>
                  </li>
                  <li className="flex items-center justify-center rounded bg-white p-3 lg:min-h-[94px] lg:p-5">
                    <p className="text-center text-sm font-bold leading-normal text-label-normal lg:text-lg">
                      AAARRR 프레임 <br className="block lg:hidden" />
                      워크를 통한 고객 <br className="block sm:hidden" />
                      획득 전략 수립
                    </p>
                  </li>
                  <li className="flex min-h-[87px] items-center justify-center rounded bg-white p-3 sm:min-h-[66px] lg:min-h-[94px] lg:p-5">
                    <p className="text-center text-sm font-bold leading-normal text-label-normal lg:text-lg">
                      마케팅 <br className="block lg:hidden" />
                      대시보드 설계
                    </p>
                  </li>
                  <li className="flex items-center justify-center rounded bg-white p-3 lg:min-h-[94px] lg:p-5">
                    <p className="text-center text-sm font-bold leading-normal text-label-normal lg:text-lg">
                      실제 기업 비즈니스 <br className="block sm:hidden" />
                      문제를 해결하는 마케팅 프로젝트 진행
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section6_주차별 커리큘럼 */}
      <section className="bg-trueGray-800 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36">
        <div className="mx-auto w-full max-w-lg lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16"
          >
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-4xl">
              누구나 데이터 친화적인
              <br />
              그로스 마케터로 성장할 수 있도록
              <br />
              설계된 4개월 커리큘럼
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
              데이터 분석 기초로 시작해 그로스 마케팅 <br className="block lg:hidden" />
              심화로 끝나는 <br className="hidden lg:block" />
              단계별 커리큘럼으로 <br className="block lg:hidden" />
              취업에 필요한 모든 것을 준비할 수 있어요.
            </p>
          </div>

          <p
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="mb-6 text-center text-lg font-bold text-label-assistive lg:mb-10 lg:text-2xl"
          >
            주차별 커리큘럼
          </p>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.curriculum_accordion_wrap} ${styles.curriculum_accordion_wrap_section6} hidden flex-row overflow-hidden rounded-lg bg-[#383838] lg:flex`}
          >
            <CurriculumTab curriculumDetailList={curriculumDetailList} />
          </div>
          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.curriculum_accordion_wrap} flex flex-col overflow-hidden lg:hidden`}
          >
            {curriculumDetailList.map((project, idx) => (
              <CurriculumAccordion contents={project} key={project.titleM} idx={idx} />
            ))}
          </ul>
        </div>
      </section>

      {/* Section7_커리큘럼 혜택 */}
      <section className="bg-gray-900 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36">
        <div className="mx-auto w-full max-w-lg lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16"
          >
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
              마케팅과 데이터 분석이
              <br />
              처음이라도 걱정하지 마세요!
            </h4>
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="hidden flex-row gap-6 lg:flex"
          >
            {curriculumBenefitList.map(curriculumBenefit => (
              <li
                className="flex flex-1 flex-col rounded-lg bg-trueGray-800"
                key={curriculumBenefit.title}
              >
                <div className="w-full">
                  <img
                    className="h-[200px] w-full object-cover"
                    src={curriculumBenefit.imgUrlPc}
                    alt="-"
                  />
                </div>
                <div className="flex flex-col p-5">
                  <h6 className="mb-1 text-lg font-bold text-yellow-450">
                    {curriculumBenefit.title}
                  </h6>
                  <p className="whitespace-pre-line text-base leading-normal text-label-assistive">
                    {curriculumBenefit.desc}
                  </p>
                  {curriculumBenefit.descList && (
                    <div className="mt-2.5 flex flex-col border-t border-label-alternative pt-2.5">
                      {curriculumBenefit.descList?.map(item => (
                        <p
                          key={`${curriculumBenefit.title}-${item.linkText}`}
                          className={`${styles.text_dot} ${styles.text_dot_section7} text-sm leading-normal text-label-assistive`}
                        >
                          <Link href={item.linkUrl} target="_blank" className="underline">
                            {item.linkText}
                          </Link>
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.section7_slider} lg:hidden`}
          >
            <CurriculumBenefitSliderItem contents={curriculumBenefitList} />
          </div>
        </div>
      </section>

      {/* Section8_기업 연계 프로젝트 */}
      <section id="project" className="bg-strong pb-[104px] pt-[88px] lg:pb-40 lg:pt-36">
        <div className="mx-auto w-full max-w-lg px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">기업 연계 프로젝트</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-5 lg:text-4xl">
              실제 기업의 비즈니스 문제를 <br className="block lg:hidden" />
              해결하고 <br className="hidden lg:block" />
              나만의 독보적인 <br className="block lg:hidden" />
              포트폴리오를 만들어요
            </h4>
            <p className="text-center text-sm leading-normal text-label-normal lg:text-lg">
              참여 기업과 함께 협업 프로젝트를 진행하면서 <br className="block lg:hidden" />
              실전에 가장 가까운 마케팅을 경험하고 <br className="hidden lg:block" />
              실무진의 <br className="block lg:hidden" />
              피드백까지 담은 포트폴리오를 완성해요.
            </p>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch lg:justify-start lg:gap-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="w-full rounded-lg border bg-[#FDFDFD] p-4 lg:w-auto lg:flex-1 lg:p-6"
            >
              <p className="mb-1 max-w-fit rounded-sm bg-[rgba(255,119,16,0.2)] px-2 py-1 text-xs font-bold text-yellow-450 lg:mb-2 lg:px-3 lg:text-sm">
                미드 프로젝트
              </p>
              <h6 className="mb-4 text-lg font-bold leading-normal text-label-normal lg:mb-5 lg:text-2xl lg:leading-snug">
                탄소배출권 거래 플랫폼 기업 연계 <br className="block lg:hidden" />
                프로젝트
              </h6>
              <div className="w-full">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section8_img1_ver2_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="cover"
                  className="!relative w-full"
                  alt="-"
                />
              </div>
              <dl className="my-4 flex flex-col gap-0.5 lg:my-5 lg:border-b lg:pb-5">
                <dt className="text-sm font-bold text-label-normal lg:text-base">주제</dt>
                <dd className="text-sm text-label-normal lg:text-base">
                  플랫폼 신규 유저 확보(User Acquisition)
                </dd>
              </dl>

              <div className="flex flex-1 flex-col rounded-lg border px-4 py-3 lg:rounded-none lg:border-none lg:p-0">
                <div
                  className={`flex flex-row items-center gap-3 ${isMobile ? 'cursor-pointer' : ''}`}
                  onClick={() =>
                    isMobile &&
                    setOpenedMidProject(openedMidProject === 'marketing' ? null : 'marketing')
                  }
                >
                  <p className="text-left text-sm font-bold text-label-normal lg:text-base">
                    마케팅 활동 내용
                  </p>
                  {isMobile && (
                    <ChevronDownIcon
                      className={`${
                        openedMidProject === 'marketing' ? 'rotate-180 transform' : ''
                      } ml-auto h-auto w-4 shrink-0 grow-0 text-label-neutral`}
                    />
                  )}
                </div>
                {(openedMidProject === 'marketing' || !isMobile) && (
                  <ul className="mt-3 flex flex-col rounded bg-alternative px-4 py-3 lg:mt-0.5 lg:bg-transparent lg:p-0">
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        고객 페르소나 정의 - 탄소배출권 구매 가능성이 높은 유저 그룹 정의 및 발굴
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        신규 고객을 위한 맞춤형 랜딩페이지 개설
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        검색 엔진 최적화(SEO) 적용
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        트래픽 확보를 위한 Paid 마케팅 진행
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        탄소배출권 구매 가이드 제작 등 트래픽 증가를 위한 콘텐츠 마케팅 병행
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        GA4 분석 및 랜딩페이지 방문자 데이터 분석을 통한 이탈율 개선 전략 도출
                      </p>
                    </li>
                  </ul>
                )}
              </div>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="800"
              className="w-full rounded-lg border bg-[#FDFDFD] p-4 lg:w-auto lg:flex-1 lg:p-6"
            >
              <p className="mb-1 max-w-fit rounded-sm bg-[rgba(255,119,16,0.2)] px-2 py-1 text-xs font-bold text-yellow-450 lg:mb-2 lg:px-3 lg:text-sm">
                파이널 프로젝트
              </p>
              <h6 className="mb-4 text-lg font-bold leading-normal text-label-normal lg:mb-5 lg:text-2xl lg:leading-snug">
                비대면 운동 관리 플랫폼 기업 연계 <br className="block lg:hidden" />
                프로젝트
              </h6>
              <div className="w-full">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section8_img2_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="cover"
                  className="!relative w-full"
                  alt="-"
                />
              </div>
              <dl className="my-4 flex flex-col gap-0.5 lg:my-5 lg:border-b lg:pb-5">
                <dt className="text-sm font-bold text-label-normal lg:text-base">주제</dt>
                <dd className="text-sm text-label-normal lg:text-base">
                  앱 서비스 마케팅 유저 이탈 방지 및 글로벌 마케팅 전략 수립(User Retension &
                  Acquisition)
                  <ul className="mt-4 flex flex-col lg:mt-1 lg:p-0">
                    <li className="border-t py-2 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        실제 이용자 데이터를 활용한 유저 이탈 방지 및 신규 프로그램 유저 확보를 위한
                        전략 수립
                      </p>
                    </li>
                    <li className="border-t pt-2 lg:border-0 lg:pt-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        해외 유저 데이터를 활용한 글로벌 마케팅 전략 제안
                      </p>
                    </li>
                  </ul>
                </dd>
              </dl>

              <div className="flex flex-1 flex-col rounded-lg border px-4 py-3 lg:rounded-none lg:border-none lg:p-0">
                <div
                  className={`flex flex-row items-center gap-3 ${isMobile ? 'cursor-pointer' : ''}`}
                  onClick={() =>
                    isMobile &&
                    setOpenedFinalProject(openedFinalProject === 'marketing' ? null : 'marketing')
                  }
                >
                  <p className="text-left text-sm font-bold text-label-normal lg:text-base">
                    마케팅 진행 내용
                  </p>
                  {isMobile && (
                    <ChevronDownIcon
                      className={`${
                        openedFinalProject === 'marketing' ? 'rotate-180 transform' : ''
                      } ml-auto h-auto w-4 shrink-0 grow-0 text-label-neutral`}
                    />
                  )}
                </div>
                {(openedFinalProject === 'marketing' || !isMobile) && (
                  <ul className="mt-3 flex flex-col rounded bg-alternative px-4 py-3 lg:mt-0.5 lg:bg-transparent lg:p-0">
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        검색 트렌드 데이터 분석 후 타켓 키워드 선정
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        Application Push 기능 활용을 통한 유저 행동 데이터 기반 개인화 메시지 최적화
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        유저 세그먼트 기반 맞춤형 이메일 설계 및 캠페인 진행
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        Paid 마케팅 및 검색 엔진 최적화(SEO) 적용
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        마케팅 툴을 활용한 광고 소재 제작
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        광고 고효율 소재 분석 및 개선안 제시
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        프로젝트 활동 관련 별도 랜딩 페이지 제작
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        유저 유입 및 재방문 데이터 기반 맞춤형 프로모션 기획
                      </p>
                    </li>
                    <li className="border-t py-1.5 first-of-type:border-0 first-of-type:pt-0 lg:border-0 lg:py-0">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-normal lg:text-base lg:text-label-neutral`}
                      >
                        랜딩페이지 방문자 데이터 분석을 통한 유입 경로 분석 및 이탈율 개선 전략 도출
                      </p>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mt-[56px] lg:mt-20"
          >
            <h6 className="mb-3 text-center text-2xl font-bold text-label-normal lg:mb-16 lg:text-4xl">
              파이널 프로젝트가 궁금하다면?
            </h6>
            <p className="mb-10 text-center leading-normal text-label-normal lg:hidden">
              5주 동안 함께 할 기업 담당자의 인터뷰를 통해
              <br />
              파이널 프로젝트로 어떤 마케팅 역량을
              <br />
              쌓을 수 있는지 확인해보세요!
            </p>

            <div className="flex flex-row overflow-hidden lg:rounded-lg">
              <div className="relative aspect-[604/371] w-full overflow-hidden rounded lg:w-auto lg:flex-1 lg:rounded-none">
                <ReactPlayer
                  alt="멋쟁이사자처럼 그로스 마케팅 부트캠프 2기"
                  url="https://www.youtube.com/watch?v=6iiUbII_mgQ"
                  controls={true}
                  width="100%"
                  height="100%"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </div>
              <div className="hidden flex-row items-center justify-center bg-white pl-12 pr-10 lg:flex lg:w-1/2">
                <p className="text-left text-xl font-bold text-label-normal">
                  5주 동안 함께 할 기업 담당자의 인터뷰를 통해
                  <br />
                  파이널 프로젝트로 어떤 마케팅 역량을 쌓을 수 있는지 확인해보세요!
                </p>
              </div>
            </div>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="1000"
            className="mt-[56px] hidden lg:mt-20 lg:block"
          >
            <h6 className="mb-2 text-center text-xl font-bold text-label-normal lg:mb-10 lg:text-2xl">
              프로젝트 진행 프로세스
            </h6>
            <ul className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:gap-6">
              <li className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6">
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 1
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  기업 유저 데이터 제공
                </p>
              </li>
              <li className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6">
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 2
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  팀 빌딩 및 팀별 마케팅 계획 수립 및 제안서 공유
                </p>
              </li>
              <li className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6">
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 3
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  기업 담당자의 주기적인 멘토링 진행
                </p>
              </li>
              <li className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6">
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 4
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  제안서 기반 마케팅 활동 진행
                </p>
              </li>
              <li className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6">
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 5
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  프로젝트 수행에 대한 결과 발표 및 기업 담당자의 피드백
                </p>
              </li>
              <li className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6">
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 6
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  우수 팀 성과물에 대해 기업 실제 마케팅에 반영
                </p>
              </li>
            </ul>
          </div>

          <div className="mt-[56px] lg:mt-20 lg:hidden">
            <h6
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="mb-2 text-center text-xl font-bold text-label-normal lg:mb-10 lg:text-2xl"
            >
              프로젝트 진행 프로세스
            </h6>
            <ul className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:gap-6">
              <li
                data-aos-once="true"
                data-aos="fade-right"
                data-aos-easing="ease"
                data-aos-duration="800"
                data-aos-delay="500"
                className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6"
              >
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 1
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  기업 유저 데이터 제공
                </p>
              </li>
              <li
                data-aos-once="true"
                data-aos="fade-right"
                data-aos-easing="ease"
                data-aos-duration="800"
                data-aos-delay="700"
                className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6"
              >
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 2
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  팀 빌딩 및 팀별 마케팅 계획 수립 및 제안서 공유
                </p>
              </li>
              <li
                data-aos-once="true"
                data-aos="fade-right"
                data-aos-easing="ease"
                data-aos-duration="800"
                data-aos-delay="900"
                className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6"
              >
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 3
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  기업 담당자의 주기적인 멘토링 진행
                </p>
              </li>
              <li
                data-aos-once="true"
                data-aos="fade-right"
                data-aos-easing="ease"
                data-aos-duration="800"
                data-aos-delay="1100"
                className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6"
              >
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 4
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  제안서 기반 마케팅 활동 진행
                </p>
              </li>
              <li
                data-aos-once="true"
                data-aos="fade-right"
                data-aos-easing="ease"
                data-aos-duration="800"
                data-aos-delay="1300"
                className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6"
              >
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 5
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  프로젝트 수행에 대한 결과 발표 및 기업 담당자의 피드백
                </p>
              </li>
              <li
                data-aos-once="true"
                data-aos="fade-right"
                data-aos-easing="ease"
                data-aos-duration="800"
                data-aos-delay="1500"
                className="flex w-full flex-row items-center gap-2 rounded-lg border bg-white px-4 py-3 lg:w-[calc(33.33%-16px)] lg:gap-3 lg:p-6"
              >
                <p className="max-w-fit shrink-0 rounded-sm bg-[rgba(255,119,16,0.2)] px-[7.5px] py-1 text-xs font-bold text-yellow-450 lg:rounded lg:px-2 lg:text-base">
                  STEP 6
                </p>
                <p className="flex-1 text-base font-bold leading-normal text-label-normal lg:text-lg">
                  우수 팀 성과물에 대해 기업 실제 마케팅에 반영
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Section9_수료후 직무 */}
      <section className="bg-trueGray-800 pb-[104px] pt-[88px] lg:pb-40 lg:pt-36">
        <div className="mx-auto w-full max-w-lg px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">수료 후 직무</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-4xl">
              4개월 뒤, 여러분 앞에 <br className="block lg:hidden" />
              활짝 열릴 취업의 문
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
              멋쟁이사자처럼 그로스 마케팅 부트캠프 과정을 <br className="block lg:hidden" />
              수료하면 이런 직무를 선택할 수 있어요.
            </p>
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="flex w-full flex-col gap-3 lg:flex-row lg:gap-6"
          >
            {section9DetailsList.map(details => (
              <li className="flex w-full flex-col items-start justify-start rounded-lg bg-[#383838] p-4 lg:lg:w-auto lg:flex-1 lg:p-6">
                <div className="flex w-full flex-row items-center justify-start gap-2 lg:gap-3">
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.2)] px-1.5 py-0.5 text-xs font-bold text-yellow-450 lg:px-2 lg:text-base">
                    {details.step}
                  </p>
                  <h6 className="text-base font-bold leading-normal text-white lg:text-xl">
                    {details.title}
                  </h6>
                </div>
                <div className="mt-2 flex w-full flex-1 flex-col gap-2 border-t border-label-neutral pt-2 lg:mt-3 lg:gap-3 lg:pt-3">
                  {details.descList?.map(item => (
                    <p
                      key={`${details.step}-${item}`}
                      className={`${styles.text_dot} ${styles.text_dot_section9} text-sm leading-normal text-label-assistive lg:text-base`}
                    >
                      {item}
                    </p>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-10 text-center text-sm leading-normal text-label-assistive lg:mt-16 lg:text-base">
            이외에도 데이터 분석을 활용하는 대표적인 직무인 <br className="block lg:hidden" />
            ’PM’ ‘PO’ ‘비즈니스 분석가’ ‘데이터 분석가’로 커리어를{' '}
            <br className="block lg:hidden" />
            확장할 수 있어요.
          </p>
        </div>
      </section>

      {/* Section10_취업지원 */}
      <section id="support" className="bg-gray-900 pb-[104px] pt-[88px] lg:pb-40 lg:pt-36">
        <div className="mx-auto w-full max-w-lg lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">취업지원</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-4xl">
              하반기 공채에 맞춰 빈틈없이
              <br />
              설계된 월별 취업지원 프로그램
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
              아직도 직무 공부 따로, 취업 준비 따로 하시나요? <br className="block lg:hidden" />
              전문 커리어 서포트팀이 <br className="hidden lg:block" />
              설계한 월별 프로그램만 따라오면 <br className="block lg:hidden" />
              올해 하반기 취업까지 문제 없어요!
            </p>
          </div>

          <ul className="hidden flex-row flex-wrap gap-6 lg:flex">
            {supportBenefitList.map(supportBenefit => (
              <li
                className="flex w-[calc(33.333333%-16px)] flex-col rounded-lg bg-trueGray-800"
                key={supportBenefit.title}
              >
                <div className="h-[222px] w-full object-cover">
                  <Image
                    src={supportBenefit.imgUrlPc}
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full"
                    alt={supportBenefit.title}
                  />
                </div>
                <div className="flex h-[252px] w-full flex-col items-start justify-start p-6">
                  <div className="mb-3 flex w-full flex-row items-center gap-2 border-b border-[#404040] pb-3">
                    <p className="max-w-fit rounded-sm bg-[rgba(255,119,16,0.2)] px-2 text-xl font-bold text-yellow-450">
                      {supportBenefit.step}
                    </p>
                    <h6 className="text-base leading-normal text-white">{supportBenefit.title}</h6>
                  </div>

                  {supportBenefit.desc && (
                    <p className="w-full whitespace-pre-line text-base leading-normal text-white">
                      {supportBenefit.desc}
                    </p>
                  )}
                  {supportBenefit.descList && (
                    <ul className="flex w-full flex-col gap-0.5">
                      {supportBenefit.descList.map(item => (
                        <li key={`${supportBenefit.title}-${item}`}>
                          <p
                            className={`${styles.text_dot} ${styles.text_dot_section10} text-base leading-normal text-white`}
                          >
                            {item}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                  {supportBenefit.point && (
                    <div className="mt-3 flex w-full flex-1 flex-col gap-1.5 rounded bg-white px-4 py-3">
                      {supportBenefit.point.map(item => (
                        <div key={`${supportBenefit.title}-${item.pointTitle}`}>
                          <p className="text-base font-bold text-label-normal">{item.pointTitle}</p>
                          <p className="text-base leading-normal text-label-normal">
                            {item.pointDesc}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.section10_slider} lg:hidden`}
          >
            <SupportBenefitSliderItem contents={supportBenefitList} />
          </div>

          <p className="mt-10 text-center text-sm leading-normal text-label-alternative lg:mt-16 lg:text-base">
            *취업 지원 프로그램의 세부 내용은 내부 상황에 따라 <br className="block lg:hidden" />
            변동될 수 있습니다.
          </p>
        </div>
      </section>

      {/* Section11_채용연계 */}
      <section className="bg-strong pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36">
        <div className="mx-auto w-full max-w-lg px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">채용연계</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-5 lg:text-4xl lg:leading-[1.4]">
              그로스 마케터 취업에 <br className="block lg:hidden" />
              강력한 부스터가 <br className="hidden lg:block" />
              되어줄 <br className="block lg:hidden" />
              역대급 채용 연계 진행
            </h4>
            <p className="text-center text-sm leading-normal text-label-normal lg:text-lg">
              오직 멋쟁이사자처럼에서만 제공하는 정규직 전환형 <br className="block lg:hidden" />
              인턴십으로 취업 성공의 열쇠를 잡아보세요.
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex flex-col overflow-hidden rounded-lg border lg:flex-row"
          >
            <div className="w-full lg:w-2/5 lg:max-w-[500px]">
              <Image
                src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section11_img_${
                  isMobile ? 'm' : 'pc'
                }.png`}
                layout="fill"
                objectFit="cover"
                className="!relative w-full"
                alt="-"
              />
            </div>
            <div className="flex w-full flex-col gap-3 bg-white p-4 lg:w-3/5 lg:flex-1 lg:items-start lg:justify-center lg:gap-2 lg:pl-[6.5%]">
              <h6 className="text-lg font-bold leading-normal text-label-normal lg:text-2xl">
                멋쟁이사자처럼의 그로스 마케팅팀 인턴십 프로그램
              </h6>
              <p className="text-sm leading-normal text-label-neutral lg:text-base">
                우수 수료생을 대상으로 그로스 마케터로서 <br className="hidden lg:block" />
                정규직 <br className="block lg:hidden" />
                전환이 가능한 인턴십 기회를 제공해요.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section12_강사진 */}
      <section id="teacher" className="bg-[#EBEBEB] pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36">
        <div className="mx-auto w-full max-w-lg px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">강사진</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-5 lg:text-4xl lg:leading-[1.4]">
              분야별 최고의 강사진과 함께
              <br />
              최고의 그로스 마케터로 성장하세요
            </h4>
            <p className="text-center text-sm leading-normal text-label-normal lg:text-lg">
              20년 경력의 데이터 전문가를 포함한 현업 강사진과 <br className="block lg:hidden" />
              현직자 멘토가 <br className="hidden lg:block" />
              그로스 마케팅 기초부터 실무 노하우까지 <br className="block lg:hidden" />
              아낌없이 알려드려요.
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex flex-col gap-4 lg:flex-row lg:gap-6"
          >
            <div className="flex w-full flex-col gap-4 rounded-lg border border-normal bg-white p-4 lg:w-auto lg:flex-1 lg:gap-6 lg:p-6">
              <div className="flex flex-row-reverse items-center justify-start gap-4 border-b pb-4 lg:flex-row lg:gap-6 lg:pb-6">
                <div className="h-20 w-20 shrink-0 lg:h-[120px] lg:w-[120px]">
                  <Image
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section12_img1.png"
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full rounded"
                    alt="최갑근 강사님 프로필 이미지"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-0.5">
                  <p className="text-sm font-bold text-yellow-450">주강사</p>
                  <p className="text-xl font-bold text-label-normal">최갑근 강사님</p>
                </div>
              </div>
              <ul className="flex flex-col gap-0.5">
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12} text-sm leading-normal text-label-normal lg:text-base`}
                >
                  현) DX교육데이터협회 교육데이터본부장(컴퓨터공학박사)
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12} text-sm leading-normal text-label-normal lg:text-base`}
                >
                  현) 한국인공지능협회 전문위원
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12} text-sm leading-normal text-label-normal lg:text-base`}
                >
                  현) S대 미디어소프트웨어학과 객원교수
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12_light} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  전) E사 부사장/개발총괄
                </li>
              </ul>
            </div>
            <div className="flex w-full flex-col gap-4 rounded-lg border border-normal bg-white p-4 lg:w-auto lg:flex-1 lg:gap-6 lg:p-6">
              <div className="flex flex-row-reverse items-center justify-start gap-4 border-b pb-4 lg:flex-row lg:gap-6 lg:pb-6">
                <div className="h-20 w-20 shrink-0 lg:h-[120px] lg:w-[120px]">
                  <Image
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section12_img2_new.png"
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full rounded"
                    alt="배차경 강사님 프로필 이미지"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-0.5">
                  <p className="text-sm font-bold text-yellow-450">특강 강사</p>
                  <p className="text-xl font-bold text-label-normal">배차경 강사님</p>
                </div>
              </div>
              <ul className="flex flex-col gap-0.5">
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12_light} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  전) 이노션 MX 전략팀 시니어 미디어 플래너
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12_light} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  전) 페이스북 코리아 Agency Partner
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12_light} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  전) 제일기획 미디어 플래너
                </li>
              </ul>
            </div>
          </div>

          <h6
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="mb-4 mt-8 text-center text-xl font-bold leading-normal text-label-normal lg:mb-6 lg:mt-12 lg:text-2xl"
          >
            현직자 멘토
          </h6>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex flex-col gap-4 lg:flex-row lg:gap-6"
          >
            <div className="flex w-full flex-col gap-4 rounded-lg border border-normal bg-white p-4 lg:w-auto lg:flex-1 lg:gap-6 lg:p-6">
              <div className="flex flex-row-reverse items-center justify-start gap-4 border-b pb-4 lg:flex-row lg:gap-6 lg:pb-6">
                <div className="h-20 w-20 shrink-0 lg:h-[120px] lg:w-[120px]">
                  <Image
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section12_img3.png"
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full rounded"
                    alt="심볼 이미지"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-0.5">
                  <p className="text-xl font-bold text-label-normal">라이크 멘토님</p>
                </div>
              </div>
              <ul className="flex flex-col gap-0.5">
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12} text-sm leading-normal text-label-normal lg:text-base`}
                >
                  현) 기후테크 스타트업 경영지원팀장, 온실가스 감축 컨설팅 업무 수행
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12} text-sm leading-normal text-label-normal lg:text-base`}
                >
                  현) 세종대학교 기후변화특성화대학원 석사과정 재학
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12_light} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  전) IT업계 관련 협회 정보보호정책팀, 신사업 조사 담당
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12_light} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  전) 엔터테인먼트 스타트업 경영지원팀, 탄소배출권 판매 관리 담당
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12_light} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  전) HR업계 중견기업, 채용지원 업무 담당
                </li>
              </ul>
            </div>
            <div className="flex w-full flex-col gap-4 rounded-lg border border-normal bg-white p-4 lg:w-auto lg:flex-1 lg:gap-6 lg:p-6">
              <div className="flex flex-row-reverse items-center justify-start gap-4 border-b pb-4 lg:flex-row lg:gap-6 lg:pb-6">
                <div className="h-20 w-20 shrink-0 lg:h-[120px] lg:w-[120px]">
                  <Image
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section12_img4.png"
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full rounded"
                    alt="심볼 이미지"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-0.5">
                  <p className="text-xl font-bold text-label-normal">라이언 멘토님</p>
                </div>
              </div>
              <ul className="flex flex-col gap-0.5">
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12} text-sm leading-normal text-label-normal lg:text-base`}
                >
                  현) 건강 관리 서비스 스타트업 CTO
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12_light} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  전) 카카오 소프트웨어 엔지니어 & 데이터 사이언티스트, GDG 판교 오거나이저
                </li>
              </ul>
            </div>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="mt-4 lg:mt-6"
          >
            <div className="flex w-full flex-col gap-4 rounded-lg border border-normal bg-white p-4 lg:gap-6 lg:p-6">
              <p className="w-full border-b pb-4 text-xl font-bold text-label-normal lg:pb-6">
                멘토링 내용
              </p>
              <ul className="flex flex-col gap-0.5">
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12} text-sm leading-normal text-label-normal lg:text-base`}
                >
                  프로젝트 진행에 대한 주기적인 멘토링
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section12} text-sm leading-normal text-label-normal lg:text-base`}
                >
                  프로젝트 수행에 대한 결과 발표 및 피드백 제공
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section13_사전설명회 */}
      {isAfterEarlySessionDate ? (
        <section className="bg-[#FFF9F5] pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36">
          <div className="mx-auto w-full max-w-lg px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16"
            >
              <p className="text-sm font-bold text-yellow-450 lg:text-lg">고민상담소</p>
              <h4 className="text-center text-2xl font-bold leading-snug text-label-normal lg:text-4xl lg:leading-[1.4]">
                실시간 라이브로 진행된
                <br />
                고민상담소 다시보기
              </h4>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="w-full overflow-hidden rounded lg:rounded-lg"
              style={{
                position: 'relative',
                paddingTop: '56.25%',
              }}
            >
              <ReactPlayer
                alt="멋쟁이사자처럼 그로스 마케팅 부트캠프 2기 사전설명회 LIVE"
                url="https://youtube.com/live/cGezftmTXT8?feature=share"
                controls={true}
                width="100%"
                height="100%"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
              />
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-[#FFF9F5] pb-[104px] pt-[88px] lg:py-40">
          <div className="mx-auto max-w-lg px-4 lg:flex lg:w-full lg:max-w-7xl lg:flex-row lg:items-stretch lg:justify-between lg:gap-6 lg:px-6">
            <div className="flex w-full flex-col lg:w-auto lg:max-w-[500px] lg:flex-1">
              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="800"
                data-aos-delay="300"
                className="mb-10 flex flex-col items-center justify-center lg:mb-6 lg:items-start"
              >
                <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-left lg:text-lg">
                  고민상담소
                </p>
                <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-6 lg:text-left lg:text-4xl lg:leading-[1.4]">
                  강사님과 함께 하는 고민상담소
                </h4>
                <p className="text-center text-sm leading-normal text-label-normal lg:text-left lg:text-base">
                  그로스 마케팅 부트캠프에 대해 궁금한 점이 많으시죠?
                  <br />
                  실시간 Q&A로 진행되는 고민상담소에서 모두 해결해보세요!
                </p>
              </div>

              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="800"
                data-aos-delay="300"
                className="flex flex-col gap-6 lg:flex-1"
              >
                <div className="block w-full lg:hidden">
                  <img
                    className="h-auto w-full overflow-hidden rounded object-cover"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section13_pre-orientation.gif"
                    alt="사전설명회 예시 이미지"
                    loading="lazy"
                  />
                </div>

                <div className="flex w-full flex-col items-center justify-center lg:flex-1 lg:items-start lg:justify-start lg:gap-1">
                  <p
                    className={`${styles.text_dot} ${styles.text_dot_section13} text-sm font-bold leading-normal text-yellow-450 lg:text-base`}
                  >
                    일시 : 4/8(화) 오후 6시 30분
                  </p>
                  <p
                    className={`${styles.text_dot} ${styles.text_dot_section13} text-sm font-bold leading-normal text-yellow-450 lg:text-base`}
                  >
                    장소 : YouTube 라이브 스트리밍
                  </p>
                </div>

                <Link
                  href="https://likelion2.typeform.com/kdtgm02"
                  target="_blank"
                  className="flex w-full items-center justify-center gap-2 rounded bg-yellow-450 py-3.5 text-sm font-bold text-white lg:max-w-[395px] lg:rounded-lg lg:py-5 lg:text-lg"
                >
                  고민상담소 신청하기
                  <img
                    className="h-4 w-4 lg:h-5 lg:w-5"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-13th/pre-orientation_icon.png"
                    alt="go link"
                  />
                </Link>
              </div>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="hidden lg:inline-block"
            >
              <img
                className="h-[394px] w-auto overflow-hidden rounded-lg object-cover"
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/section13_pre-orientation.gif"
                alt="사전설명회 예시 이미지"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      {/* Section14_교육혜택 */}
      <section className="bg-[#333333] pb-[104px] pt-[88px] lg:pb-40 lg:pt-36">
        <div className="mx-auto w-full max-w-lg lg:max-w-none">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16"
          >
            <p className="text-sm font-bold text-yellow-450 lg:text-lg">교육 혜택</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-4xl">
              오직 그로스 마케터가 되겠다는
              <br />
              열정만 준비하세요!
            </h4>
            <p className="text-center text-sm leading-normal text-white lg:text-lg">
              나머지는 멋쟁이사자처럼에서 모두 지원해 드릴게요.
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.section14_slider}`}
          >
            <BenefitSliderItem contents={benefitList} />
          </div>
        </div>
      </section>

      {/* Section15_수강 후기 */}
      <section className="bg-gray-900 pb-[104px] pt-[88px] lg:py-40 lg:pt-36">
        <div className="mx-auto w-full max-w-lg lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-12 flex flex-col items-center justify-center gap-2 lg:mb-16 lg:gap-4"
          >
            <p className="text-sm font-bold text-yellow-450 lg:text-lg">수료생 후기</p>
            <h4 className="text-center text-xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
              높은 교육 만족도를 보여주는
              <br />
              수강생들의 생생한 후기
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
              아래 후기는 교육 만족도 조사에서 수강생들이 직접 작성한{' '}
              <br className="block lg:hidden" />
              내용이며 <br className="hidden lg:block" />
              띄어쓰기 조차 수정하지 않은 리얼 후기에요.
            </p>
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="hidden w-full flex-col gap-6 lg:flex"
          >
            {reviewList.map(reviews => (
              <li className="flex w-full flex-row items-center gap-6 rounded-lg bg-trueGray-800 px-6 py-5">
                <div className="border-r border-[#525252] pr-6">
                  <img className="h-[84px] w-[84px] overflow-hidden" src={reviews.imgUrl} alt="-" />
                </div>
                <div className="flex flex-1 flex-col gap-1 lg:gap-2">
                  <p className="whitespace-pre-line text-xl font-bold leading-snug text-white">
                    {reviews.review}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.section15_slider} lg:hidden`}
          >
            <ReviewSliderItem contents={reviewList} />
          </div>
        </div>
      </section>

      {/* Section16_지원및선발일정 */}
      <section id="schedule" className="bg-[#FFF2E9] pb-[72px] pt-[88px] lg:pb-[100px] lg:pt-36">
        <div className="mx-auto flex w-full max-w-lg flex-col gap-10 px-4 lg:mx-auto lg:max-w-7xl lg:flex-row lg:gap-6 lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="w-full lg:w-auto lg:flex-1"
          >
            <div className="mb-6 lg:mb-8">
              <h4 className="text-center text-xl font-bold text-label-normal lg:text-3xl">
                지원 및 선발 일정
              </h4>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="flex flex-col"
            >
              <div className="relative flex w-full flex-col lg:hidden">
                <dl className="flex flex-col gap-2 rounded-lg bg-white px-4 py-3">
                  <dt className="w-full text-sm font-semibold leading-normal text-yellow-450">
                    모집기간
                  </dt>
                  <dd className="whitespace-pre-line text-sm leading-normal text-label-normal">
                    {applyStart} ~ {applyEnd}
                  </dd>
                </dl>
              </div>

              <div className="relative mt-3 flex w-full flex-col lg:hidden">
                <div className="flex flex-col gap-2 rounded-lg bg-white px-4 py-3">
                  {(ApplyScheduleList as ApplyScheduleItem[]).map(edu => (
                    <dl
                      key={edu.title}
                      className="flex flex-col gap-2 border-b border-[#E0DFDE] pb-2 last-of-type:border-0 last-of-type:pb-0"
                    >
                      <dt className="w-full text-sm font-semibold leading-normal text-yellow-450">
                        {edu.title}
                      </dt>
                      <dd className="whitespace-pre-line text-sm leading-normal text-label-normal">
                        <div
                          className={`flex flex-row items-center gap-4 ${
                            edu.contentList ? 'cursor-pointer' : ''
                          }`}
                          onClick={() =>
                            setOpenedTitle(openedTitle === edu.title ? null : edu.title)
                          }
                        >
                          {edu.content}
                          {edu.contentList && (
                            <ChevronDownIcon
                              className={`${
                                openedTitle === edu.title ? 'rotate-180 transform' : ''
                              } ml-auto h-auto w-4 shrink-0 grow-0 text-label-neutral lg:w-5`}
                            />
                          )}
                        </div>
                        {edu.contentList && openedTitle === edu.title && (
                          <ul className="mt-2 flex flex-col lg:mt-3">
                            {edu.contentList.map((item: string) => (
                              <li key={item} className="border-t py-2 last-of-type:pb-0">
                                <p
                                  className={`${styles.text_dot} ${styles.text_dot_section16} text-sm leading-normal text-label-normal lg:text-base`}
                                >
                                  {item}
                                </p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </dd>
                    </dl>
                  ))}
                </div>
              </div>

              <div className="relative hidden w-full flex-col lg:flex">
                <div className="rounded-lg bg-white px-6 py-5 lg:min-h-[283px]">
                  <dl className="flex flex-row items-center gap-5 border-b border-[#E0DFDE] py-3 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0">
                    <dt className="w-[95px] whitespace-pre-line text-base font-bold leading-normal text-yellow-450">
                      모집기간
                    </dt>
                    <dd className="flex-1 whitespace-pre-line text-left text-base leading-normal text-label-normal">
                      {applyStart} ~ {applyEnd}
                    </dd>
                  </dl>

                  {(ApplyScheduleList as ApplyScheduleItem[]).map(edu => (
                    <dl
                      className="flex flex-row items-start gap-5 border-b border-[#E0DFDE] py-3 first-of-type:items-center first-of-type:pt-0 last-of-type:items-center last-of-type:border-0 last-of-type:pb-0"
                      key={edu.title}
                    >
                      <dt className="w-[95px] whitespace-pre-line text-base font-bold leading-normal text-yellow-450">
                        {edu.title}
                      </dt>
                      <dd className="flex-1 whitespace-pre-line text-left text-base leading-normal text-label-normal">
                        <div
                          className={`flex flex-row items-center gap-4 ${
                            edu.contentList ? 'mt-3 cursor-pointer' : ''
                          }`}
                          onClick={() =>
                            setOpenedTitle(openedTitle === edu.title ? null : edu.title)
                          }
                        >
                          {edu.content}
                          {edu.contentList && (
                            <ChevronDownIcon
                              className={`${
                                openedTitle === edu.title ? 'rotate-180 transform' : ''
                              } ml-auto h-auto w-4 shrink-0 grow-0 text-label-neutral lg:w-5`}
                            />
                          )}
                        </div>
                        {edu.contentList && openedTitle === edu.title && (
                          <ul className="mt-2 flex flex-col lg:mt-5">
                            {edu.contentList.map((item: string) => (
                              <li key={item} className="border-t py-2 last-of-type:pb-0">
                                <p
                                  className={`${styles.text_dot} ${styles.text_dot_section16} text-sm leading-normal text-label-normal lg:text-base`}
                                >
                                  {item}
                                </p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </dd>
                    </dl>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="w-full lg:w-auto lg:flex-1"
          >
            <div className="mb-6 lg:mb-8">
              <h4 className="text-center text-xl font-bold text-label-normal lg:text-3xl">
                교육일정
              </h4>
            </div>

            <ul
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="rounded-lg bg-white px-5 py-4 lg:min-h-[439px] lg:px-6 lg:py-5"
            >
              <li className="flex flex-row items-start justify-center gap-5 border-b border-[#E0DFDE] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:gap-4 lg:py-3">
                <b className="w-[53px] shrink-0 whitespace-pre-line text-sm font-semibold leading-normal text-yellow-450 lg:w-[76px] lg:text-base lg:font-bold">
                  교육 기간
                </b>
                <p className="flex-1 whitespace-pre-line text-sm leading-normal text-label-normal lg:text-base">
                  {start} ~ {end}
                </p>
              </li>
              <li className="flex flex-row items-start justify-center gap-5 border-b border-[#E0DFDE] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:gap-4 lg:py-3">
                <b className="w-[53px] shrink-0 whitespace-pre-line text-sm font-semibold leading-normal text-yellow-450 lg:w-[76px] lg:text-base lg:font-bold">
                  교육 시간
                </b>
                <p className="flex-1 whitespace-pre-line text-sm leading-normal text-label-normal lg:text-base">
                  {totalHours}시간 / 평일 9시 ~ 18시
                  <br />
                  (점심시간 12시 ~ 13시 제외)
                </p>
              </li>
              {EduScheduleList.map(edu => (
                <li
                  className="flex flex-row items-start justify-center gap-5 border-b border-[#E0DFDE] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:gap-4 lg:py-3"
                  key={edu.title}
                >
                  <b className="w-[53px] shrink-0 whitespace-pre-line text-sm font-semibold leading-normal text-yellow-450 lg:w-[76px] lg:text-base lg:font-bold">
                    {edu.title}
                  </b>
                  <p className="flex-1 whitespace-pre-line text-sm leading-normal text-label-normal lg:text-base">
                    {edu.content}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* section17_자주묻는질문 */}
      <section className="bg-white pb-24 pt-20 lg:pb-40 lg:pt-36">
        <div
          data-aos-once="true"
          data-aos="fade-up"
          data-aos-easing="ease"
          data-aos-duration="800"
          data-aos-delay="300"
          className="mx-auto w-full max-w-7xl px-4 lg:px-6"
        >
          <h4 className="mb-6 text-center text-xl font-bold text-label-normal lg:text-4xl">
            자주 묻는 질문
          </h4>
          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mx-auto mb-10 flex max-w-[324px] flex-wrap items-center justify-center gap-1.5 lg:mb-16 lg:max-w-none lg:flex-nowrap lg:gap-2"
          >
            {bootCampFaqTabList.map(tabItem => {
              const isActive = tabItem.value === faqTab
              return (
                <li
                  className={`${
                    isActive
                      ? 'bg-yellow-450 text-white'
                      : 'border border-normal bg-white text-gray-400'
                  } cursor-pointer rounded-full px-5 py-1.5 text-xs font-bold lg:px-6 lg:py-2 lg:text-sm`}
                  onClick={() => onClickFaqTab(tabItem.value)}
                  key={tabItem.value}
                >
                  {tabItem.label}
                </li>
              )
            })}
          </ul>
          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="mx-auto flex flex-col"
          >
            {faqList.map(faq => (
              <li
                className={`${styles.faqItem} border-b border-[#E5E5E5] py-4 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:py-6`}
                key={faq.question}
              >
                <details className="group overflow-hidden">
                  <summary className="cursor-pointer list-none group-open:pb-1 lg:group-open:pb-2">
                    <div className="flex w-full items-center justify-between gap-4">
                      <div>
                        <p
                          className={`${styles.faq_item_title} text-sm font-semibold text-label-normal lg:text-lg`}
                        >
                          {faq.question}
                        </p>
                      </div>
                      <ChevronDownIcon className="w-4 shrink-0 text-gray-500 group-open:rotate-180 lg:w-6" />
                    </div>
                  </summary>
                  <div className="pl-5 lg:pl-[22px]">
                    <div className="whitespace-pre-wrap text-sm leading-normal text-gray-500 lg:text-base">
                      {faq.answer && (
                        <span className="max-w-full">
                          {faq.answer}
                          {faq.externalUrl1 && (
                            <>
                              {' '}
                              자세한 발급 과정은 아래 링크를 통해 확인 가능합니다.
                              <br />-{' '}
                              <Link href={faq.externalUrl1} target="_blank" className="underline">
                                {' '}
                                {faq.urlName1}
                              </Link>
                              {faq.secondAnswer}
                            </>
                          )}
                          {faq.externalUrl2 && (
                            <>
                              <br />-{' '}
                              <Link href={faq.externalUrl2} target="_blank" className="underline">
                                {' '}
                                {faq.urlName2}
                              </Link>
                            </>
                          )}
                        </span>
                      )}
                      {faq.answerList &&
                        faq.answerList.map(el => (
                          <p key={el} className="text-sm text-gray-500 lg:text-base">
                            {el}
                          </p>
                        ))}
                    </div>
                  </div>
                </details>
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-col items-center justify-between gap-3 rounded-md bg-strong p-4 lg:mt-16 lg:flex-row lg:items-center lg:rounded-lg lg:p-10">
            <p className="flex-1 text-center text-sm font-semibold text-label-normal lg:text-left lg:text-lg lg:font-bold">
              더 궁금한 점이 있다면 <br className="block lg:hidden" />
              언제든지 교육 매니저에게 물어보세요!
            </p>
            <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row">
              <Link
                href="https://likelion.channel.io/workflows/705420"
                target="_blank"
                className="flex h-[53px] w-full flex-row items-center justify-center gap-2 rounded bg-yellow-450 px-3 text-sm font-bold text-white lg:h-[67px] lg:w-[395px] lg:flex-1 lg:gap-2 lg:rounded-lg lg:text-lg"
              >
                1:1 문의하기
                <img
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-growth-2nd/faq_icon_btn.png"
                  className="h-4 w-4 shrink-0 object-contain lg:h-5 lg:w-5"
                  alt="1:1 문의하기"
                />
              </Link>
              <div
                className="flex h-[53px] w-full shrink-0 cursor-pointer flex-row items-center justify-center gap-2 rounded bg-[#301704] lg:h-[67px] lg:w-[67px] lg:rounded-lg"
                onClick={() => copyUrlShare('https://bootcamp.likelion.net/school/kdt-growth-2nd')}
              >
                <span className="text-sm font-bold text-white lg:hidden">링크 복사하기</span>
                <img
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-13th/faq_icon_share.png"
                  className="h-4 w-4 shrink-0 object-contain lg:h-8 lg:w-8"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default GrowthKlass2nd
